import { useMemo, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { SplitButton } from 'primereact/splitbutton';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useExportCryoBabyLabelsMutation } from '@/app/services/aspirations';
import { useFeatureFlag } from '@/app/services/features';
import { AspirationStats, StopGoIcon, StopGoIconStatus } from '@/components';
import { AspirationSite, AspirationsToday } from '@/rtk-query.gql';
import {
  useQcDataQuery,
  useRefreshStopGoSign,
  useShowStopSignQuery,
} from '@/services/opu-search/hooks';
import { QcDataResponse } from '@/services/opu-search/types';
import {
  useAspirationFormReport,
  useOocyteCollectionFormReport,
} from '@/services/reports';
import { formatDateWithPattern } from '@/utils';

import { useOpuSearchContext } from '../../hooks/useOpuContext';
import { QC_REQUIRED_FIELDS_OPU_SEARCH } from '../../utils/constants';
import { OpuSearchQcInformationDialog } from '../OpuSearchQcInformationDialog';
import { PoolAspirationsDialog } from '../PoolAspirationsDialog';

type OpuSearchActionsProps = {
  refetch: () => void;
  poolNumArray: number[];
  aspirations?: AspirationsToday[];
  siteData?: AspirationSite[];
};
export const OpuSearchActions = ({
  refetch,
  poolNumArray,
  aspirations,
  siteData,
}: OpuSearchActionsProps) => {
  const { t } = useTranslation();
  const { setQcDataSubmitted } = useOpuSearchContext();
  const [params] = useSearchParams();
  const opuDate =
    params.get('date') || formatDateWithPattern(new Date(), 'MM-dd-yyyy');

  const opuSiteId = params.get('site');
  const ivmTechnicianId = params.get('tech');
  const aspirationId = params.get('id');
  const location = params.get('location') as string;

  const [showAspirationStats, setShowAspirationStats] = useState(false);
  const [showPoolAspirations, setShowPoolAspirations] = useState(false);
  const [showQcInformation, setShowQcInformation] = useState(false);
  const qcDialogRef = useRef<Dialog>(null);
  const [qcDialogContainer, setQcDialogContainer] = useState<HTMLElement>();

  const oocyteCollectionFormFromScheduleEnabled = useFeatureFlag(
    'Oocyte_Collection_Form_From_Schedule',
  );
  const { mutateAsync: refreshStopSign } = useRefreshStopGoSign();
  const [cryoBabyLabels] = useExportCryoBabyLabelsMutation();
  const [generateOocyteCollectionForm] = useOocyteCollectionFormReport();
  const [generateAspirationForm] = useAspirationFormReport();
  const { data: qcData, refetch: refetchQcData } = useQcDataQuery({
    siteId: opuSiteId,
    date: opuDate,
    location,
  });

  const { data: showStopSignValue, refetch: refetchShowStopSign } =
    useShowStopSignQuery({ siteId: opuSiteId });

  const handleMatingLabels = async () => {
    try {
      const labels = aspirations?.map((a) => ({
        accountCode: a.account,
        donorName: a.donor,
        donorRegNum: a.donorRegNum,
        export: a.export,
        nonEuComment: a.nonEuComment,
      }));

      const matchedSite = siteData?.find((site) => site.id === opuSiteId);
      const siteName = matchedSite?.name;

      const result = await cryoBabyLabels({
        labels,
        aspDate: opuDate,
        aspSite: siteName,
        location: location,
      });

      if (result.data && result.data.exportCryoBabyLabelXlsx) {
        const excelBytes = result.data.exportCryoBabyLabelXlsx;
        const blob = new Blob([new Uint8Array(excelBytes)], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const excelUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = excelUrl;
        link.download = 'Cryo Baby Labels For Shipping - OPU Search.xlsx';
        link.click();
        setTimeout(() => URL.revokeObjectURL(excelUrl), 100);
      } else {
        throw new Error('No Excel data received from the server');
      }
    } catch (error) {
      throw new Error(`Error creating export labels: ${error}`);
    }
  };

  const qcDataSubmitted = useMemo(() => {
    if (!qcData) return false;
    return QC_REQUIRED_FIELDS_OPU_SEARCH.every(
      (field) =>
        qcData[field as keyof typeof qcData] !== null &&
        qcData[field as keyof typeof qcData] !== '',
    );
  }, [qcData]);

  useMemo(() => {
    setQcDataSubmitted(qcDataSubmitted);
  }, [qcDataSubmitted, setQcDataSubmitted]);

  const aspirationIds =
    aspirations
      ?.map((a) => a.aspirationId)
      .filter((id): id is string => !!id) ?? [];

  const oocyteCollectionFormDisabled = oocyteCollectionFormFromScheduleEnabled
    ? !opuDate || !opuSiteId
    : !aspirationIds.length;
  const printLabelsMenuItems = [
    {
      label: t('opuSearch.printOocyteCollectionForm'),
      disabled: oocyteCollectionFormDisabled,
      command: () => {
        if (oocyteCollectionFormFromScheduleEnabled) {
          generateOocyteCollectionForm({
            aspirationIds,
            opuDate,
            opuSiteId: opuSiteId as string,
            location,
          });
          return;
        }

        generateOocyteCollectionForm({
          aspirationIds,
        });
      },
    },
    {
      label: t('opuSearch.printAspirationForm'),
      disabled: !aspirationIds.length,
      command: () =>
        generateAspirationForm({
          aspirationIds,
        }),
    },
  ];

  return (
    <div className="opu-search-actions">
      {!aspirationId && (
        <>
          {printLabelsMenuItems.length ? (
            <SplitButton
              severity="secondary"
              label={t('labels.printLabels')}
              onClick={() => {
                handleMatingLabels();
              }}
              disabled={!opuDate || !opuSiteId || !ivmTechnicianId}
              model={printLabelsMenuItems}
            />
          ) : (
            <Button
              severity="secondary"
              label={t('labels.printLabels')}
              onClick={() => {
                handleMatingLabels();
              }}
              disabled={!opuDate || !opuSiteId || !ivmTechnicianId}
            />
          )}
          <Button
            severity="secondary"
            label={t('qc.qcInformation')}
            onClick={() => setShowQcInformation(true)}
            disabled={!opuDate || !opuSiteId || !ivmTechnicianId}
          />
          {showQcInformation && (
            <Dialog
              ref={qcDialogRef}
              header={t('qc.qcInformation')}
              visible={showQcInformation}
              onHide={() => setShowQcInformation(false)}
              style={{ width: '60vw' }}
              onShow={() =>
                setQcDialogContainer(
                  qcDialogRef.current?.getMask() ?? undefined,
                )
              }
            >
              <OpuSearchQcInformationDialog
                container={qcDialogContainer}
                setShowQcInformation={setShowQcInformation}
                qcData={qcData as QcDataResponse}
                refetchQcData={refetchQcData}
              />
            </Dialog>
          )}
        </>
      )}
      <Button
        severity="secondary"
        label={t('aspiration.showAspirationStats')}
        onClick={() => setShowAspirationStats(true)}
        className="show-aspiration-stats"
        disabled={!opuDate || !opuSiteId || !ivmTechnicianId}
      />
      {showAspirationStats && (
        <Dialog
          header={t('opuSearch.aspirationStats')}
          visible={showAspirationStats}
          onHide={() => setShowAspirationStats(false)}
          footer={
            <div>
              <Button
                severity="secondary"
                label={t('common.close')}
                icon="pi pi-times"
                onClick={() => setShowAspirationStats(false)}
              />
            </div>
          }
        >
          <AspirationStats
            aspirationDate={opuDate}
            siteId={opuSiteId as string}
          />
        </Dialog>
      )}
      <Button
        severity="secondary"
        label={t('opuSearch.poolAspirations')}
        onClick={() => setShowPoolAspirations(true)}
        disabled={!opuDate || !opuSiteId || !ivmTechnicianId}
      />
      {showPoolAspirations && (
        <Dialog
          header={t('opuSearch.poolAspirations')}
          visible={showPoolAspirations}
          onHide={() => setShowPoolAspirations(false)}
        >
          <PoolAspirationsDialog
            setShowPoolAspirations={setShowPoolAspirations}
            refetch={refetch}
            poolNumArray={poolNumArray}
          />
        </Dialog>
      )}
      <span
        className="stop-go-sign pivot-3d"
        onClick={async () => {
          await refreshStopSign({
            val: (!!showStopSignValue).toString(),
            siteId: opuSiteId as string,
          });
          refetchShowStopSign();
        }}
      >
        <StopGoIcon
          status={
            showStopSignValue === true
              ? StopGoIconStatus.STOP
              : StopGoIconStatus.GO
          }
        />
      </span>
    </div>
  );
};
